"use client";

import * as React from "react";
import { ArrowRight, FlaskConical, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { decodeHtmlEntities } from "@/lib/utils";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { normalizeEcommerceProduct } from "@/integrations/wordpress/ecommerceProductUtils";
import { getProductImageAlt, useProductImageAlts } from "@/lib/productImageUtils";
import { isEligibleVial } from "@/lib/packPricing";
import { getBuildYourPackLinkProps } from "@/lib/pageLinks";
declare global {
  const wvcClient: any;
}
export interface CartUpsellProps {
  cartProductIds: number[];
  cartQuantitiesByProductId?: Record<number, number>;
  onNavigate: () => void;
  busy: boolean;
}
interface UpsellProduct {
  id: number;
  displayName: string;
  strengthLabel?: string;
  formattedPrice: string;
  imageUrl?: string;
  imageAlt: string;
  rawProduct: any;
  maxQuantity?: number;
  currentInCart: number;
}

/**
 * Remove trailing mg strength from title for clean display.
 * e.g. "Semax-10mg" -> "Semax", "Tesamorelin — 10 mg" -> "Tesamorelin", "TEK-2 (TR)-60mg" -> "TEK-2 (TR)"
 */
function stripStrengthFromTitle(rawTitle: string): string {
  const decoded = decodeHtmlEntities(rawTitle || "").trim();
  if (!decoded) return "";
  return decoded.replace(/\s*(?:[-–—|,:]\s*)?(?:\d+(?:\.\d+)?\s*mg\b)(?:\s*(?:\+|\/|&)\s*\d+(?:\.\d+)?\s*mg\b)*\s*$/i, "").replace(/\s*[-–—|,:]\s*$/, "").trim();
}

/**
 * Extract single mg strength e.g. "10 mg", "40 mg", "60 mg"
 */
function extractStrengthLabel(rawTitle: string): string | undefined {
  const decoded = decodeHtmlEntities(rawTitle || "").trim();
  if (!decoded) return undefined;
  const match = decoded.match(/(\d+(?:\.\d+)?)\s*mg\b/i);
  if (!match) return undefined;
  return `${match[1]} mg`;
}
export default function CartUpsell({
  cartProductIds,
  cartQuantitiesByProductId = {},
  onNavigate,
  busy
}: CartUpsellProps) {
  // Always hook up media library alts hook at top level
  useProductImageAlts();
  const [catalogProducts, setCatalogProducts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [addingId, setAddingId] = React.useState<number | null>(null);
  const [addErrorMap, setAddErrorMap] = React.useState<Record<number, string>>({});

  // Synchronous ref lock to prevent duplicate taps while adding
  const addingLockRef = React.useRef(false);

  // Fetch catalog products once on mount
  React.useEffect(() => {
    let mounted = true;
    async function loadUpsellCatalog() {
      if (typeof wvcClient === "undefined") {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const queryVars: Record<string, any> = {
          post_type: "product",
          per_page: 100
        };
        const wpQuery = new WP_Query(queryVars);
        wpQuery.parse_query_vars();
        let rawPosts: any[] = [];
        if (typeof wvcClient.get_wp_query_results === "function") {
          const res = await wvcClient.get_wp_query_results(wpQuery.toJSON(), {
            embeds: ["wp:featuredmedia"],
            fields: []
          }, false);
          rawPosts = res?.posts ?? [];
        } else if (typeof wvcClient.getStoreProducts === "function") {
          const res = await wvcClient.getStoreProducts(queryVars);
          rawPosts = Array.isArray(res) ? res : res?.products ?? [];
        }
        if (mounted) {
          const normalized = rawPosts.map(raw => normalizeEcommerceProduct(raw));
          setCatalogProducts(normalized);
        }
      } catch (err) {
        // Never show an error for a failed suggestion load
        console.warn("[CartUpsell] Failed to load catalog products:", err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }
    void loadUpsellCatalog();
    return () => {
      mounted = false;
    };
  }, []);

  // Filter to eligible single-vial simple products not in cart, keep catalog order, take first 2
  const upsellItems: UpsellProduct[] = React.useMemo(() => {
    if (!catalogProducts || catalogProducts.length === 0) return [];
    const excludedIds = new Set(cartProductIds.map(Number));
    const candidates = catalogProducts.filter(product => {
      if (!product || typeof product !== "object") return false;

      // Exclude if already in cart
      const prodId = Number(product.id);
      if (excludedIds.has(prodId)) return false;

      // Simple products only (never variable products unless valid variation id is sent)
      const prodType = String(product.type || "simple").toLowerCase();
      if (prodType !== "simple") return false;

      // In stock & purchasable check
      if (product.is_in_stock === false) return false;
      if (product.is_purchasable === false) return false;

      // Single-vial eligibility check (excludes combos like BPC-157 + TB-500)
      const eligibility = isEligibleVial(product);
      if (!eligibility.eligible) return false;

      // Respect server max quantity vs quantity already in cart (if somehow present)
      const currentInCart = cartQuantitiesByProductId[prodId] ?? 0;
      const maxRaw = product.quantity_limits?.maximum ?? product.max_purchase_quantity ?? product.stock_quantity;
      const maxQty = typeof maxRaw === "number" && !Number.isNaN(maxRaw) && maxRaw > 0 ? maxRaw : undefined;
      if (typeof maxQty === "number" && currentInCart >= maxQty) {
        return false;
      }
      return true;
    });
    return candidates.slice(0, 2).map(prod => {
      const prodId = Number(prod.id);
      const rawTitle = String(prod.title || prod.name || "Research Compound");
      const displayName = stripStrengthFromTitle(rawTitle) || decodeHtmlEntities(rawTitle);
      const strength = extractStrengthLabel(rawTitle);
      const formattedPrice = prod.formatted_discounted_price || prod.formatted_price || (typeof prod.price === "number" ? `$${prod.price.toFixed(2)}` : "$0.00");
      const imageUrl = prod.featured_image_url || prod.images?.[0]?.src || undefined;
      const imageAlt = getProductImageAlt(prod, imageUrl);
      const maxRaw = prod.quantity_limits?.maximum ?? prod.max_purchase_quantity ?? prod.stock_quantity;
      const maxQty = typeof maxRaw === "number" && !Number.isNaN(maxRaw) && maxRaw > 0 ? maxRaw : undefined;
      const currentInCart = cartQuantitiesByProductId[prodId] ?? 0;
      return {
        id: prodId,
        displayName,
        strengthLabel: strength,
        formattedPrice,
        imageUrl,
        imageAlt,
        rawProduct: prod,
        maxQuantity: maxQty,
        currentInCart
      };
    });
  }, [catalogProducts, cartProductIds, cartQuantitiesByProductId]);

  // Handle add to cart
  const handleAddToCart = async (item: UpsellProduct) => {
    if (addingLockRef.current || busy || addingId !== null) return;

    // Check quantity limits
    if (typeof item.maxQuantity === "number" && item.currentInCart >= item.maxQuantity) {
      setAddErrorMap(prev => ({
        ...prev,
        [item.id]: `Maximum purchase limit of ${item.maxQuantity} reached.`
      }));
      return;
    }
    if (typeof wvcClient === "undefined" || !wvcClient?.cart?.addToCart) {
      setAddErrorMap(prev => ({
        ...prev,
        [item.id]: "Cart functionality currently unavailable."
      }));
      return;
    }
    addingLockRef.current = true;
    setAddingId(item.id);
    setAddErrorMap(prev => {
      const next = {
        ...prev
      };
      delete next[item.id];
      return next;
    });
    try {
      const response = await wvcClient.cart.addToCart({
        id: item.id,
        quantity: 1
      });
      if (response?.success) {
        // Dispatch standard event on document so HeaderCart refreshes and stays open
        document.dispatchEvent(new Event("wvc_cart_updated"));
      } else {
        const rawMessage = String(response?.message || "Could not add. Please try again.");
        const cleanMsg = rawMessage.replace(/<[^>]*>?/gm, "").trim();
        setAddErrorMap(prev => ({
          ...prev,
          [item.id]: cleanMsg || "Could not add. Please try again."
        }));
      }
    } catch (err: any) {
      const rawMsg = err?.message || "Could not add. Please try again.";
      const cleanMsg = String(rawMsg).replace(/<[^>]*>?/gm, "").trim();
      setAddErrorMap(prev => ({
        ...prev,
        [item.id]: cleanMsg || "Could not add. Please try again."
      }));
    } finally {
      addingLockRef.current = false;
      setAddingId(null);
    }
  };
  const showProductSection = loading || upsellItems.length > 0;
  const packLinkProps = getBuildYourPackLinkProps();
  return <div className="mt-4 rounded-2xl border border-[#e2eaf0] bg-[#f7fafb] p-3.5 text-[#172126]">
      {/* Product Upsell Rows */}
      {showProductSection && <>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
            MORE FROM THE COLLECTION
          </p>

          <div className="mt-2.5 space-y-2.5">
            {loading ? (/* Loading Skeletons (2 rows) */
        Array.from({
          length: 2
        }).map((_, idx) => <div key={idx} className="flex items-center gap-3 animate-pulse">
                  <div className="h-16 w-12 shrink-0 rounded-md bg-[#eef3f5]" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-3.5 w-3/5 rounded bg-[#e2eaf0]" />
                    <div className="h-3 w-2/5 rounded bg-[#e2eaf0]" />
                  </div>
                  <div className="h-9 w-20 shrink-0 rounded-full bg-[#e2eaf0]" />
                </div>)) : upsellItems.map(item => {
          const isAddingThis = addingId === item.id;
          const isDisabled = busy || addingId !== null;
          const err = addErrorMap[item.id];
          const buttonAriaLabel = item.strengthLabel ? `Add ${item.displayName} ${item.strengthLabel} to cart` : `Add ${item.displayName} to cart`;
          return <div key={item.id} className="space-y-1">
                    <div className="flex items-center gap-3">
                      {/* 48x64px thumbnail box */}
                      <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#dce8ed] bg-[#eef3f5] p-1">
                        {item.imageUrl ? <img src={item.imageUrl} alt={item.imageAlt || item.displayName} loading="lazy" decoding="async" className="size-full object-contain" /> : <FlaskConical className="size-5 text-[#94a3b8]" strokeWidth={1.5} aria-hidden="true" />}
                      </div>

                      {/* Middle info */}
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-[#111827]">
                          {item.displayName}
                        </p>
                        <p className="mt-0.5 text-xs text-[#64748b]">
                          {item.strengthLabel ? `${item.strengthLabel} · ${item.formattedPrice}` : item.formattedPrice}
                        </p>
                      </div>

                      {/* Add button with minimum 44px tap target */}
                      <button type="button" onClick={() => handleAddToCart(item)} disabled={isDisabled} aria-label={buttonAriaLabel} className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-full border border-[#cbd9df] bg-white px-4 text-xs font-semibold text-[#111827] transition-colors hover:border-[#087db3] hover:text-[#087db3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168fc4] disabled:cursor-not-allowed disabled:opacity-50">
                        {isAddingThis ? <Loader2 className="size-3.5 animate-spin text-[#087db3]" aria-hidden="true" /> : "+ Add"}
                      </button>
                    </div>

                    {err && <p role="alert" className="text-xs text-red-700 pl-[60px]">
                        {err}
                      </p>}
                  </div>;
        })}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-[#e2eaf0]" aria-hidden="true" />
        </>}

      {/* Tappable link row (always present) */}
      <Link {...packLinkProps} onClick={onNavigate} className="group flex min-h-[44px] items-center justify-between gap-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168fc4] rounded-lg">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[#111827]">
            Building a bigger collection?
          </p>
          <p className="mt-0.5 text-xs font-medium text-[#087db3] group-hover:underline">
            Build your own pack →
          </p>
        </div>
        <ArrowRight className="size-4 shrink-0 text-[#087db3] transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
      </Link>
    </div>;
}